import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { EventTask } from "../../model/EventTask"
import { FoodEventTask } from '../../model/FoodEventTask'
import { SendMoneyTask } from '../../model/SendMoneyTask'
import { WrappedEventTask } from '../../model/WrappedEventTask'
import { saveEventTaskAsync } from './currentEventTaskAPI'
import { RootState } from '../../app/store';
import { CurrentPartyEventState } from '../currentPartyEvent/currentPartyEventSlice'
import { currencyAsNumber } from '../../app/controls/CurrencyInput'
import { sanitizeEmail } from '../../utils/emailUtils'

export interface CurrentEventTaskState {
     id: number
     title: string
     eventTaskType: number
     status: 'idle' | 'loading' | 'failed'
     amountAsText: string
     itemName: string
     numberOfItemsRequired: number
     allergyRequirements: string
     notes: string
     allocatedUserEmail: string 
     progressionStatus: number
     allocatedUserAlias: string | null
}

const initialState: CurrentEventTaskState = {
     id: 0,
     title: '',
     eventTaskType: 0,
     status: 'idle',
     amountAsText: '£0',
     itemName: '',
     numberOfItemsRequired: 0,
     allergyRequirements: '',
     notes: '',
     allocatedUserEmail: '',
     progressionStatus: 0,
     allocatedUserAlias: null
}

export interface SaveEventTaskRequest {
     token: string
     userEmail: string
   }

   export interface AllocateEventTaskRequest {
     token: string
     email: string
   }

export interface SaveEventTaskDto {
     partyEventId: number
     eventTaskType: number
     eventTask: EventTask
     userEmail: string
     status: number
}
export interface AllocateEventTaskDto {
   eventTaskId: number
   email: string
}


export const storeEventTaskAsync = createAsyncThunk(
     'currentEventTask/saveEventTask',
     async (request: SaveEventTaskRequest, { getState }) => {
       const { currentEventTask } = getState() as { currentEventTask: CurrentEventTaskState}
       const { currentPartyEvent } = getState() as { currentPartyEvent: CurrentPartyEventState}
       let saveEventTaskDto: SaveEventTaskDto = {
         partyEventId: currentPartyEvent.partyEvent?.id ?? 0,
         eventTaskType: currentEventTask.eventTaskType,
         status: currentEventTask.progressionStatus,
         eventTask: {
             id: currentEventTask.id,
             title: currentEventTask.title,
             allocatedUserEmail: currentEventTask.allocatedUserEmail,
             notes: currentEventTask.notes,
             allocatedUserAlias: currentEventTask.allocatedUserAlias,
             description: '',
             status: currentEventTask.progressionStatus
         },
         userEmail: request.userEmail
       }
       switch (currentEventTask.eventTaskType) {
         case 1:
           let foodTask: FoodEventTask = {
             id: currentEventTask.id,
             title: currentEventTask.title,
allocatedUserEmail:currentEventTask.allocatedUserEmail,
allocatedUserAlias:currentEventTask.allocatedUserAlias,
             itemName: currentEventTask.itemName,
             numberOfItemsRequired: currentEventTask.numberOfItemsRequired,
             allergyRequirements: currentEventTask.allergyRequirements,
             notes: currentEventTask.notes,
             description: '',
             status: currentEventTask.progressionStatus
           }
           saveEventTaskDto.eventTask = foodTask
           break
         case 2:
           let moneyTask: SendMoneyTask = {
             id: currentEventTask.id,
             title: currentEventTask.title,
allocatedUserEmail:currentEventTask.allocatedUserEmail,
allocatedUserAlias:currentEventTask.allocatedUserAlias,
             amount: currencyAsNumber(currentEventTask.amountAsText),
             notes: currentEventTask.notes,
             description: '',
             status: currentEventTask.progressionStatus
           }
           saveEventTaskDto.eventTask = moneyTask
           break
       } 
       const response = await saveEventTaskAsync(saveEventTaskDto, request.token)

       if (response != null) {
         switch (currentEventTask.eventTaskType) {
           case 0:
             let simpleTask: WrappedEventTask = {
               eventTaskType: currentEventTask.eventTaskType,
               eventTask: response
             }
             return simpleTask
           case 1:
             let foodTask: WrappedEventTask = {
               eventTaskType: currentEventTask.eventTaskType,
               eventTask: response as FoodEventTask
             }
             return foodTask
           case 2:
             let moneyTask: WrappedEventTask = {
               eventTaskType: currentEventTask.eventTaskType,
               eventTask: response as SendMoneyTask
             }
             return moneyTask
         }
       }
       return null;
     }
)

export const allocateEventTaskAsync = createAsyncThunk(
   'currentEventTask/allocateEventTask',
   async (request: AllocateEventTaskRequest, { getState }) => {
     const { currentEventTask } = getState() as { currentEventTask: CurrentEventTaskState}
     let allocateEventTaskDto: AllocateEventTaskDto = {
       eventTaskId: currentEventTask.id,
       email: request.email
     }
    return request.email;
   }
)

export const currentEventTaskSlice = createSlice({
     name: 'currentEventTask',
     initialState: initialState,
     reducers: {
         associateCurrentEventTask: (state, action: PayloadAction<WrappedEventTask>) => {
           state.id = action.payload.eventTask.id
           state.title = action.payload.eventTask.title
           state.allocatedUserEmail = action.payload.eventTask.allocatedUserEmail ?? ''
           state.allocatedUserAlias = action.payload.eventTask.allocatedUserAlias
           state.status = 'idle'
           state.allergyRequirements = ''
           state.itemName = ''
           state.numberOfItemsRequired = 0
           state.notes = action.payload.eventTask.notes
           state.amountAsText = '£0'
           state.eventTaskType = action.payload.eventTaskType
           state.progressionStatus = action.payload.eventTask.status
           switch (action.payload.eventTaskType) {
               case 1:
                 var foodTask = action.payload.eventTask as FoodEventTask
                 state.allergyRequirements = foodTask.allergyRequirements
                 state.itemName = foodTask.itemName
                 state.numberOfItemsRequired = foodTask.numberOfItemsRequired
                 break
               case 2:
                 var moneyTask = action.payload.eventTask as SendMoneyTask
                 state.amountAsText = '£' + moneyTask.amount
                 break
           }
         },
         setTitle: (state, action: PayloadAction<string>) => {
           state.title = action.payload
         },
         setEventTitle: (state, action: PayloadAction<string>) => {
             state.eventTaskType = parseInt(action.payload, 10)
         },
         setItemName: (state, action: PayloadAction<string>) => {
           state.itemName = action.payload
         },
         setNumberOfItemsRequired: (state, action: PayloadAction<string>) => {
           try {
             state.numberOfItemsRequired = parseInt(action.payload, 10)
           } catch (error) {
             console.log(error)
           }

         },
         setAllergyRequirements: (state, action: PayloadAction<string>) => {
           state.allergyRequirements = action.payload
         },
         setNotes: (state, action: PayloadAction<string>) => {
           state.notes = action.payload
         },
         setAmount: (state, action: PayloadAction<string>) => {
           state.amountAsText = action.payload
         },
         setAllocatedUserEmail: (state, action: PayloadAction<string>) => {
           state.allocatedUserEmail = sanitizeEmail(action.payload)
         },
         setAllocatedUserAlias: (state, action: PayloadAction<string>) => {
          state.allocatedUserAlias = action.payload
         },
         setProgressionStatus: (state, action: PayloadAction<number>) => {
          state.progressionStatus = action.payload
         },
     },
     // The `extraReducers` field lets the slice handle actions defined elsewhere,
     // including actions generated by createAsyncThunk or in other slices.
     extraReducers: (builder) => {
       builder
         .addCase(storeEventTaskAsync.pending, (state) => {
           state.status = 'loading';
         })
         .addCase(storeEventTaskAsync.fulfilled, (state, action) => {
           state.status = 'idle';
           state.id = action.payload?.eventTask.id ?? state.id
         })
         .addCase(storeEventTaskAsync.rejected, (state) => {
           state.status = 'failed';
         })
         .addCase(allocateEventTaskAsync.pending, (state) => {
           state.status = 'loading';
         })
         .addCase(allocateEventTaskAsync.fulfilled, (state, action) => {
           state.status = 'idle';
           if (action.payload ) {
             state.allocatedUserEmail = sanitizeEmail(action.payload)
           }
         })
         .addCase(allocateEventTaskAsync.rejected, (state) => {
           state.status = 'failed';
         }
       );
     },
})

export const selectCurrentEventTaskId = (state: RootState) => state.currentEventTask.id;
export const selectCurrentEventTaskTitle = (state: RootState) => state.currentEventTask.title;
export const selectCurrentEventTaskTitleEmpty = (state: RootState) => state.currentEventTask.title === '';
export const selectCurrentEventTaskStatus = (state: RootState) => state.currentEventTask.status;
export const selectCurrentEventTaskType = (state: RootState) => state.currentEventTask.eventTaskType;
export const selectCurrentNumberOfItemsRequired = (state: RootState) => state.currentEventTask.numberOfItemsRequired;
export const selectCurrentAllergyRequirements = (state: RootState) => state.currentEventTask.allergyRequirements;
export const selectCurrentNotes = (state: RootState) => state.currentEventTask.notes;
export const selectCurrentEventTaskItemName = (state: RootState) => state.currentEventTask.itemName;
export const selectCurrentEventTaskAmountAsText = (state: RootState) => state.currentEventTask.amountAsText;
export const selectCurrentEventTaskAllocatedUserEmail = (state: RootState) => state.currentEventTask.allocatedUserEmail;
export const selectCurrentEventTaskAllocatedUserAlias = (state: RootState) => state.currentEventTask.allocatedUserAlias
export const selectProgressionStatus = (state: RootState) => state.currentEventTask.progressionStatus
export const { associateCurrentEventTask, setTitle, setEventTitle, 
setItemName, setNumberOfItemsRequired, setAllergyRequirements, setNotes, 
setAmount, setAllocatedUserEmail, setAllocatedUserAlias, setProgressionStatus } = currentEventTaskSlice.actions
export default currentEventTaskSlice.reducer
